import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Lock, User, Mail, Loader2, AlertTriangle } from "lucide-react";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AcceptInvitePage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { setAuth } = useAuth();
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/auth/invite/${token}`);
        setInvite(data);
      } catch (err) {
        setError(err?.response?.data?.detail || "Invitation is invalid or expired");
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/auth/accept-invite`, {
        token,
        name: name.trim() || invite.email.split("@")[0],
        password,
      });
      setAuth(data.token, data.user);
      navigate("/", { replace: true });
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to accept invite");
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-950 px-6">
      <div className="w-full max-w-sm">
        <div className="flex justify-center mb-8">
          <img
            src="https://customer-assets.emergentagent.com/job_shoot-organizer-1/artifacts/3jmotfdo_WC%20Logo%202018%20FULL%20Outlined%20WHITE.png"
            alt="Whiplash Film & Creative"
            className="h-8"
          />
        </div>
        <div className="border border-zinc-800 bg-zinc-900/60 rounded-md p-7">
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-6 h-6 text-amber-500 animate-spin" />
            </div>
          ) : !invite ? (
            <div className="text-center py-2">
              <AlertTriangle className="w-10 h-10 mx-auto text-rose-400 mb-3" />
              <h1 className="font-heading text-xl text-zinc-100 mb-2">Invitation issue</h1>
              <p className="text-sm text-zinc-500">{error}</p>
            </div>
          ) : (
            <>
              <div className="text-overline text-amber-500 mb-1">Invitation</div>
              <h1 className="font-heading text-2xl text-zinc-100 mb-1">Accept your invite</h1>
              <p className="text-sm text-zinc-500 mb-5">
                You&apos;re invited as a <strong>{invite.role}</strong>
                {invite.project_name ? <> for project <strong>&ldquo;{invite.project_name}&rdquo;</strong></> : " to the workspace"}.
              </p>
              <form onSubmit={onSubmit} className="space-y-3">
                <div className="relative">
                  <Mail className="w-3.5 h-3.5 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
                  <Input
                    value={invite.email}
                    disabled
                    className="bg-zinc-950 border-zinc-800 pl-9 text-zinc-500"
                  />
                </div>
                <div className="relative">
                  <User className="w-3.5 h-3.5 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
                  <Input
                    data-testid="accept-name-input"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Your name"
                    autoFocus
                    className="bg-zinc-950 border-zinc-800 focus-visible:ring-amber-500 pl-9"
                  />
                </div>
                <div className="relative">
                  <Lock className="w-3.5 h-3.5 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
                  <Input
                    data-testid="accept-password-input"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Choose a password (min 8 chars)"
                    className="bg-zinc-950 border-zinc-800 focus-visible:ring-amber-500 pl-9"
                  />
                </div>
                {error && (
                  <div className="text-xs text-rose-400 bg-rose-500/10 border border-rose-500/30 rounded-sm px-3 py-2">
                    {error}
                  </div>
                )}
                <Button
                  data-testid="accept-submit-btn"
                  type="submit"
                  disabled={submitting || !password}
                  className="w-full bg-amber-500 text-black hover:bg-amber-400 rounded-sm font-semibold"
                >
                  {submitting ? (
                    <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Joining…</>
                  ) : (
                    "Accept & sign in"
                  )}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
